import { useCallback, useMemo, useState } from 'react';
import type { FilterState, FilterValue } from '../components/ui/tableTemplate/TableTemplateTypes';

export interface DraftFilters<T> {
  /** The panel's working copy (scoped to `scopedKeys`). */
  draft: FilterState<T>;
  setDraftFilter: (key: keyof T, value: FilterValue) => void;
  clearDraftFilter: (key: keyof T) => void;
  /** Commit the draft onto the applied state (merge over the scoped keys). */
  apply: () => void;
  /** Empty the draft. */
  reset: () => void;
  /** Re-seed the draft from the currently applied state (call on panel open). */
  reseed: () => void;
  /** True when the draft differs from the applied scoped filters. */
  hasUnapplied: boolean;
}

/** Ordered, undefined-stripped projection of a FilterState onto `keys`. */
function project<T>(source: FilterState<T>, keys: (keyof T)[]): [keyof T, FilterValue][] {
  return keys.filter(k => source[k] !== undefined).map(k => [k, source[k] as FilterValue]);
}

/**
 * Deferred-filter layer over a live `FilterState`: the panel edits a **draft**
 * (scoped to `scopedKeys` — the advanced-section fields), and `apply()` **merges**
 * it onto the applied state via the caller's `setFilter`/`clearFilter`. The live
 * column-filter row (which writes non-scoped keys directly) is never clobbered.
 *
 * `apply()` sets each scoped key present in the draft and clears scoped keys the
 * draft dropped — leaving keys outside `scopedKeys` untouched. `reseed()` (on
 * panel open) refreshes the draft from the applied truth; closing without Apply
 * simply discards the draft (next open reseeds).
 */
export function useDraftFilters<T>(
  applied: FilterState<T>,
  scopedKeys: (keyof T)[],
  setFilter: (key: keyof T, value: FilterValue) => void,
  clearFilter: (key: keyof T) => void,
): DraftFilters<T> {
  const scoped = useCallback(
    (src: FilterState<T>): FilterState<T> =>
      Object.fromEntries(project(src, scopedKeys)) as FilterState<T>,
    [scopedKeys],
  );

  const [draft, setDraft] = useState<FilterState<T>>(() => scoped(applied));

  const setDraftFilter = useCallback((key: keyof T, value: FilterValue) => {
    setDraft(d => ({ ...d, [key]: value }));
  }, []);

  const clearDraftFilter = useCallback((key: keyof T) => {
    setDraft(d => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }, []);

  const reseed = useCallback(() => setDraft(scoped(applied)), [scoped, applied]);
  const reset = useCallback(() => setDraft({}), []);

  const apply = useCallback(() => {
    scopedKeys.forEach(key => {
      const value = draft[key];
      if (value !== undefined) setFilter(key, value);
      else if (applied[key] !== undefined) clearFilter(key);
    });
  }, [scopedKeys, draft, applied, setFilter, clearFilter]);

  const hasUnapplied = useMemo(
    () => JSON.stringify(project(applied, scopedKeys)) !== JSON.stringify(project(draft, scopedKeys)),
    [applied, draft, scopedKeys],
  );

  return { draft, setDraftFilter, clearDraftFilter, apply, reset, reseed, hasUnapplied };
}
